import React, { useState } from 'react';
import { Plus, Edit, Trash2, Save, X, Upload, Copy } from 'lucide-react';
import { useTrucks, useTruckMutations } from '../hooks/useTrucks';
import { LoadingPage } from '../components/LoadingSpinner';
import LoadingSpinner from '../components/LoadingSpinner';

const AdminPage = () => {
  const { trucks, loading, error, refetch } = useTrucks();
  const { createTruck, updateTruck, deleteTruck, loading: mutationLoading } = useTruckMutations();
  const [isEditing, setIsEditing] = useState(false);
  const [editingTruck, setEditingTruck] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    make: '',
    model: '',
    bodytype: '',
    description: '',
    uniquestockid: '',
    images: [],
    videos: [],
    cabtype: '',
    axle: '',
    price: '',
    year: '',
    power: '',
    weight: '',
    mileage: '',
    emissions: '',
    available_for_hire: false,
    hire_daily_rate: null,
    hire_weekly_rate: null,
    hire_monthly_rate: null,
    hire_status: 'available',
    is_sold: false
  });

  const resetForm = () => {
    setFormData({
      title: '',
      make: '',
      model: '',
      bodytype: '',
      description: '',
      uniquestockid: '',
      images: [],
      videos: [],
      cabtype: '',
      axle: '',
      price: '',
      year: '',
      power: '',
      weight: '',
      mileage: '',
      emissions: '',
      available_for_hire: false,
      hire_daily_rate: null,
      hire_weekly_rate: null,
      hire_monthly_rate: null,
      hire_status: 'available',
      is_sold: false
    });
  };

  const handleEdit = (truck) => {
    setEditingTruck(truck);
    setFormData({ ...truck });
    setIsEditing(true);
    setShowAddForm(true);
    // Scroll to top to show the edit form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (truckId) => {
    if (window.confirm('Are you sure you want to delete this truck?')) {
      try {
        await deleteTruck(truckId);
        refetch(); // Refresh the truck list
      } catch (error) {
        console.error('Error deleting truck:', error);
      }
    }
  };

  const handleDuplicate = (truck) => {
    // Generate new unique stock ID for duplicate
    const timestamp = Date.now();
    const newStockId = `${truck.uniquestockid}-COPY-${timestamp}`;
    
    // Create duplicate with new stock ID and reset some fields
    const duplicateData = {
      ...truck,
      id: undefined, // Remove ID so it creates a new entry
      uniquestockid: newStockId,
      title: `${truck.title} (Copy)`
    };
    
    setEditingTruck(null);
    setFormData(duplicateData);
    setIsEditing(false);
    setShowAddForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    
    try {
      // Prepare truck data with proper type conversions
      const truckData = {
        ...formData,
        price: formData.price ? parseInt(formData.price) : null,
        year: formData.year ? parseInt(formData.year) : null,
        weight: formData.weight ? parseFloat(formData.weight) : null,
        mileage: formData.mileage ? parseInt(formData.mileage) : null,
        hire_daily_rate: formData.hire_daily_rate ? parseInt(formData.hire_daily_rate) : null,
        hire_weekly_rate: formData.hire_weekly_rate ? parseInt(formData.hire_weekly_rate) : null,
        hire_monthly_rate: formData.hire_monthly_rate ? parseInt(formData.hire_monthly_rate) : null,
        images: formData.images.length > 0 ? formData.images : ['https://customer-assets.emergentagent.com/job_truckdealership/artifacts/7kr9f0qz_truckImage.png']
      };

      if (isEditing) {
        // Update existing truck
        await updateTruck(editingTruck.id, truckData);
      } else {
        // Add new truck
        await createTruck(truckData);
      }
      
      // Reset form and refresh data
      setIsEditing(false);
      setEditingTruck(null);
      setShowAddForm(false);
      resetForm();
      refetch();
      
    } catch (error) {
      setSubmitError(error.message);
      console.error('Error saving truck:', error);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditingTruck(null);
    setShowAddForm(false);
    resetForm();
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    try {
      const imageUrls = await Promise.all(
        files.map(file => {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
        })
      );
      
      setFormData({ 
        ...formData, 
        images: [...formData.images, ...imageUrls] 
      });
    } catch (error) {
      console.error('Error uploading images:', error);
      alert('Error uploading images. Please try again.');
    }
  };

  const handleVideoUpload = (e) => {
    const files = Array.from(e.target.files);
    const videoUrls = files.map(file => URL.createObjectURL(file));
    setFormData({ ...formData, videos: [...formData.videos, ...videoUrls] });
  };

  const removeImage = (index) => {
    setFormData({
      ...formData,
      images: formData.images.filter((_, i) => i !== index)
    });
  };

  const removeVideo = (index) => {
    setFormData({
      ...formData,
      videos: formData.videos.filter((_, i) => i !== index)
    });
  };

  if (loading) {
    return <LoadingPage message="Loading admin panel..." />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Error Loading Admin Panel</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={refetch}
            className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-black text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl lg:text-4xl font-black font-inter">
                Admin <span className="text-orange-500">Panel</span>
              </h1>
              <p className="text-gray-300 mt-2 font-inter">
                Manage your truck inventory with ease.
              </p>
            </div>
            <button
              onClick={() => {
                setShowAddForm(true);
                setIsEditing(false);
                resetForm();
              }}
              className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded font-bold flex items-center gap-2 transition-colors"
            >
              <Plus className="h-5 w-5" />
              Add New Truck
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Add/Edit Form */}
        {showAddForm && (
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-black text-black mb-6 font-inter">
              {isEditing ? 'Edit Truck' : 'Add New Truck'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {submitError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-600 font-inter">{submitError}</p>
                </div>
              )}
              {/* Required Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">
                    Title *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">
                    Make *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.make}
                    onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">
                    Model *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">
                    Body Type *
                  </label>
                  <select
                    required
                    value={formData.bodytype}
                    onChange={(e) => setFormData({ ...formData, bodytype: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter"
                  >
                    <option value="">Select Body Type</option>
                    <option value="Tractor Unit">Tractor Unit</option>
                    <option value="Rigid">Rigid</option>
                    <option value="Chassis Cab">Chassis Cab</option>
                    <option value="Tipper">Tipper</option>
                    <option value="Curtainside">Curtainside</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">
                    Unique Stock ID *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.uniquestockid}
                    onChange={(e) => setFormData({ ...formData, uniquestockid: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">
                  Description *
                </label>
                <textarea
                  required
                  rows={4}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter"
                />
              </div>

              {/* Optional Fields */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">
                    Cab Type
                  </label>
                  <select
                    value={formData.cabtype}
                    onChange={(e) => setFormData({ ...formData, cabtype: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter"
                  >
                    <option value="">Select Cab Type</option>
                    <option value="Day Cab">Day Cab</option>
                    <option value="Sleeper Cab">Sleeper Cab</option>
                    <option value="Crew Cab">Crew Cab</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">
                    Axle Configuration
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., 4x2, 6x2"
                    value={formData.axle}
                    onChange={(e) => setFormData({ ...formData, axle: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">
                    Price (£)
                  </label>
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">
                    Year
                  </label>
                  <input
                    type="number"
                    min="1990"
                    max="2025"
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">
                    Power
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., 450 HP"
                    value={formData.power}
                    onChange={(e) => setFormData({ ...formData, power: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">
                    Weight (tonnes)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.weight}
                    onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">
                    Mileage (km)
                  </label>
                  <input
                    type="number"
                    value={formData.mileage}
                    onChange={(e) => setFormData({ ...formData, mileage: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">
                    Emissions
                  </label>
                  <select
                    value={formData.emissions}
                    onChange={(e) => setFormData({ ...formData, emissions: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter"
                  >
                    <option value="">Select Emissions</option>
                    <option value="Euro-3">Euro-3</option>
                    <option value="Euro-4">Euro-4</option>
                    <option value="Euro-5">Euro-5</option>
                    <option value="Euro-6">Euro-6</option>
                    <option value="Electric">Electric</option>
                    <option value="Hybrid">Hybrid</option>
                  </select>
                </div>
                

              </div>

              {/* Images */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">
                  Images
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                  <div className="text-center">
                    <Upload className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="mt-4">
                      <label htmlFor="file-upload" className="cursor-pointer">
                        <span className="mt-2 block text-sm font-medium text-gray-900 font-inter">
                          Click to upload images
                        </span>
                        <input
                          id="file-upload"
                          name="file-upload"
                          type="file"
                          multiple
                          accept="image/*"
                          className="sr-only"
                          onChange={handleImageUpload}
                        />
                      </label>
                    </div>
                  </div>
                  
                  {formData.images.length > 0 && (
                    <div className="mt-4 grid grid-cols-4 gap-4">
                      {formData.images.map((image, index) => (
                        <div key={index} className="relative">
                          <img 
                            src={image} 
                            alt={`Truck ${index + 1}`}
                            className="w-full h-20 object-cover rounded"
                          />
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 hover:bg-red-700"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Image Upload Guidelines */}
                <div className="mt-3 text-xs text-gray-600 font-inter space-y-1">
                  <p><strong>Image Upload Guidelines:</strong></p>
                  <ul className="list-disc list-inside space-y-0.5">
                    <li>Recommended size: 1200x800 pixels for truck photos</li>
                    <li>Upload multiple angles: exterior, interior, engine bay, etc.</li>
                    <li>Max file size: 10MB per image (will be automatically optimised)</li>
                    <li>Supported formats: JPG, PNG, GIF, WebP</li>
                    <li>First image will be used as the main listing photo</li>
                    <li>High-quality, well-lit photos improve customer engagement</li>
                  </ul>
                </div>
              </div>

              {/* Videos */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">
                  Videos (YouTube URLs or Upload)
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                  <div className="text-center">
                    <Upload className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="mt-4">
                      <label htmlFor="video-upload" className="cursor-pointer">
                        <span className="mt-2 block text-sm font-medium text-gray-900 font-inter">
                          Click to upload videos or paste YouTube URLs
                        </span>
                        <input
                          id="video-upload"
                          name="video-upload"
                          type="file"
                          multiple
                          accept="video/*"
                          className="sr-only"
                          onChange={handleVideoUpload}
                        />
                      </label>
                      <div className="mt-2">
                        <input
                          type="url"
                          placeholder="Or paste YouTube URL here"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && e.target.value.trim()) {
                              e.preventDefault();
                              setFormData({
                                ...formData,
                                videos: [...formData.videos, e.target.value.trim()]
                              });
                              e.target.value = '';
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  
                  {formData.videos.length > 0 && (
                    <div className="mt-4 space-y-2">
                      {formData.videos.map((video, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded">
                          <div className="flex items-center space-x-3">
                            <div className="bg-red-600 text-white p-2 rounded">
                              <svg className="h-4 w-4 fill-current" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z"/>
                              </svg>
                            </div>
                            <span className="text-sm text-gray-700 truncate max-w-xs">
                              {video.startsWith('blob:') ? 'Uploaded Video' : video}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeVideo(index)}
                            className="bg-red-600 text-white rounded-full p-1 hover:bg-red-700"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Video Upload Guidelines */}
                <div className="mt-3 text-xs text-gray-600 font-inter space-y-1">
                  <p><strong>Video Upload Guidelines:</strong></p>
                  <ul className="list-disc list-inside space-y-0.5">
                    <li>Supported formats: MP4, WebM, MOV (recommended: MP4)</li>
                    <li>Max file size: 50MB per video</li>
                    <li>Recommended length: 30 seconds to 2 minutes</li>
                    <li>YouTube URLs are automatically supported</li>
                    <li>Show truck starting, driving, and key features</li>
                    <li>Good lighting and stable footage improve viewing experience</li>
                  </ul>
                  <p className="text-orange-600 font-semibold">💡 Tip: Videos showcasing the truck in action significantly increase customer interest.</p>
                </div>
              </div>

              {/* Sale Status */}
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <svg className="h-5 w-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Sale Status
                </h3>

                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                  <div className="flex items-center mb-3">
                    <input
                      type="checkbox"
                      id="is_sold"
                      data-testid="admin-mark-sold-checkbox"
                      checked={formData.is_sold || false}
                      onChange={(e) => setFormData({ ...formData, is_sold: e.target.checked })}
                      className="w-4 h-4 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500"
                    />
                    <label htmlFor="is_sold" className="ml-2 text-sm font-bold text-gray-700 font-inter">
                      Mark as Sold
                    </label>
                  </div>
                  <p className="text-sm text-gray-600 font-inter">
                    When ticked, this vehicle remains visible across the site but is overlaid with a prominent SOLD ribbon and removed from the sale enquiry call-to-actions.
                  </p>
                </div>
              </div>

              {/* Vehicle Hire Management */}
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <svg className="h-5 w-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Vehicle Hire Settings
                </h3>
                
                <div className="bg-orange-50 p-4 rounded-lg mb-6">
                  <div className="flex items-center mb-3">
                    <input
                      type="checkbox"
                      id="available_for_hire"
                      checked={formData.available_for_hire || false}
                      onChange={(e) => setFormData({ ...formData, available_for_hire: e.target.checked })}
                      className="w-4 h-4 text-orange-600 bg-gray-100 border-gray-300 rounded focus:ring-orange-500"
                    />
                    <label htmlFor="available_for_hire" className="ml-2 text-sm font-bold text-gray-700 font-inter">
                      Available for Hire
                    </label>
                  </div>
                  <p className="text-sm text-gray-600 font-inter">
                    Enable this to make the vehicle available on the "Vehicles for Hire" page
                  </p>
                </div>

                {formData.available_for_hire && (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">
                        Daily Rate (£)
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={formData.hire_daily_rate || ''}
                        onChange={(e) => setFormData({ ...formData, hire_daily_rate: e.target.value ? parseInt(e.target.value) : null })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter"
                        placeholder="120"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">
                        Weekly Rate (£)
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={formData.hire_weekly_rate || ''}
                        onChange={(e) => setFormData({ ...formData, hire_weekly_rate: e.target.value ? parseInt(e.target.value) : null })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter"
                        placeholder="650"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">
                        Monthly Rate (£)
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={formData.hire_monthly_rate || ''}
                        onChange={(e) => setFormData({ ...formData, hire_monthly_rate: e.target.value ? parseInt(e.target.value) : null })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter"
                        placeholder="2400"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">
                        Hire Status
                      </label>
                      <select
                        value={formData.hire_status || 'available'}
                        onChange={(e) => setFormData({ ...formData, hire_status: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter"
                      >
                        <option value="available">Available</option>
                        <option value="hired">Currently Hired</option>
                        <option value="maintenance">Under Maintenance</option>
                      </select>
                    </div>
                  </div>
                )}


              </div>

              {/* Form Actions */}
              <div className="flex gap-4">
                <button
                  type="submit"
                  disabled={mutationLoading}
                  className="bg-orange-600 hover:bg-orange-700 disabled:bg-orange-400 text-white px-6 py-3 rounded font-bold flex items-center gap-2 transition-colors"
                >
                  {mutationLoading ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <Save className="h-5 w-5" />
                  )}
                  {isEditing ? 'Update Truck' : 'Add Truck'}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-bold flex items-center gap-2 transition-colors"
                >
                  <X className="h-5 w-5" />
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Trucks List */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-2xl font-black text-black font-inter">
              Current Inventory ({trucks.length} trucks)
            </h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Truck
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {trucks.map((truck) => (
                  <tr key={truck.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="relative">
                          <img 
                            src={truck.images[0]} 
                            alt={truck.title}
                            className={`h-12 w-16 object-cover rounded ${truck.is_sold ? 'grayscale opacity-70' : ''}`}
                          />
                          {truck.is_sold && (
                            <span className="absolute inset-0 flex items-center justify-center bg-red-600/70 text-white text-[10px] font-black uppercase tracking-widest rounded">
                              Sold
                            </span>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-bold text-gray-900 font-inter line-clamp-1">
                            {truck.title}
                          </div>
                          <div className="text-sm text-gray-500 font-inter">
                            ID: {truck.uniquestockid}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 font-inter">
                        {truck.year} {truck.make} {truck.model}
                      </div>
                      <div className="text-sm text-gray-500 font-inter">
                        {truck.mileage?.toLocaleString()} km • {truck.emissions}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-bold text-gray-900 font-inter">
                        {truck.price ? `£${truck.price.toLocaleString()}` : 'POA'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(truck)}
                          className="text-orange-600 hover:text-orange-900 p-1"
                          title="Edit Truck"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDuplicate(truck)}
                          className="text-blue-600 hover:text-blue-900 p-1"
                          title="Duplicate Truck"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(truck.id)}
                          className="text-red-600 hover:text-red-900 p-1"
                          title="Delete Truck"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;